import { Button, Input, Modal, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Customer, CustomerAdvancePaymentVo, User } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { CustomerAdvancePayment } from "@/entity/expenseManage/customerAdvancePayment";
import { CUSTOMER_ADVANCE_PAY_STATE } from "@/meta/expenseManage/customerAdvancePayment";
import { getInfoFromCustomer } from "@/utils/getInfoFromCustomer";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";

export interface UserAccountAdvanceDetailPageProps {
    advancePaymentId?: number | string;
    loadDetail?: (id: number | string) => Promise<CustomerAdvancePaymentVo>;
    revokeAdvancePayment?: (params: { id: number | string; remark: string }) => Promise<unknown>;
}

/** 复用 Vue 预付款详情接口。 */
export async function loadUserAdvancePaymentDetail(id: number | string) {
    const response = await request.service.get<CustomerAdvancePaymentVo>(`/customer/advance-payment/vo/${id}`);
    return response.data;
}

/** 复用 Vue 预付款撤销接口。 */
export async function revokeUserAdvancePaymentDetail(params: { id: number | string; remark: string }) {
    const response = await request.service.put("/customer/advance-payment/revoke", params);
    return response.data;
}

const formatAmount = (value?: number | null) =>
    value == null
        ? DEFAULT_VALUE
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 费用管理-用户账户-预付款详情，保留 Vue 详情字段与成功状态撤销动作。 */
export function UserAccountAdvanceDetailPage({
    advancePaymentId,
    loadDetail = loadUserAdvancePaymentDetail,
    revokeAdvancePayment = revokeUserAdvancePaymentDetail
}: UserAccountAdvanceDetailPageProps) {
    const routeParams = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const currentId = advancePaymentId ?? routeParams.id ?? "";
    const [detail, setDetail] = useState<CustomerAdvancePaymentVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [revokeOpen, setRevokeOpen] = useState(false);
    const [revokeReason, setRevokeReason] = useState("");
    const [revokeError, setRevokeError] = useState("");
    const [revokeLoading, setRevokeLoading] = useState(false);

    const refreshDetail = () => {
        if (!currentId) return Promise.resolve();
        return loadDetail(currentId)
            .then(value => setDetail(value))
            .catch(() => setErrorMessage("预付款详情加载失败"));
    };

    useEffect(() => {
        void refreshDetail();
        // 详情 ID 改变时重新读取，保留 Vue 页面按路由参数加载的行为。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentId, loadDetail]);

    const payment = detail?.customerAdvancePayment;
    const customer = detail?.customer as Customer | undefined;
    const operator = detail?.user as User | undefined;

    const submitRevoke = async () => {
        if (!payment) return;
        if (!revokeReason.trim()) {
            setRevokeError("请输入撤销原因");
            return;
        }
        setRevokeLoading(true);
        try {
            await revokeAdvancePayment({ id: payment.id, remark: revokeReason.trim() });
            setRevokeOpen(false);
            setRevokeReason("");
            setRevokeError("");
            await refreshDetail();
        } catch {
            setRevokeError("预付款撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    /** 按 Vue Grid/BaseItem 契约渲染平台或预付款银行账号。 */
    const renderBankAccount = (title: string, account?: CustomerAdvancePayment["platformAccount"]) => (
        <div className="section-card">
            <div className="section-title">{title}</div>
            <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                <ZhBaseItem label="开户名称" value={account?.name || DEFAULT_VALUE} />
                <ZhBaseItem label="银行账号" value={account?.account || DEFAULT_VALUE} />
                <ZhBaseItem label="开户行" value={account?.openingBank || DEFAULT_VALUE} />
            </ZhGrid>
        </div>
    );

    return (
        <section aria-label="user-account-advance-detail-page" className="flex min-h-full flex-col gap-4">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: payment?.id || DEFAULT_VALUE }]}
                    rightInfos={[{ label: "创建时间", value: formatDateTime(payment?.createdTime) }]}
                    stateDesc={CUSTOMER_ADVANCE_PAY_STATE.getIntroByCode(payment?.state) || DEFAULT_VALUE}
                    title="预付款记录"
                />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            </div>
            <div className="section-card">
                <div className="section-title">账户信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                    <ZhBaseItem label="用户" value={getInfoFromCustomer(customer)} />
                </ZhGrid>
            </div>
            <div className="section-card">
                <div className="section-title">预付款信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                    <ZhBaseItem label="金额" value={`${formatAmount(payment?.amount)}元`} />
                    <ZhBaseItem label="日期" value={payment?.date || DEFAULT_VALUE} />
                </ZhGrid>
            </div>
            {renderBankAccount("平台银行账号", payment?.platformAccount)}
            {renderBankAccount("预付款账号", payment?.advancePaymentAccount)}
            <div className="section-card">
                <div className="section-title">操作信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                    <ZhBaseItem label="操作人" value={operator?.name || DEFAULT_VALUE} />
                    <ZhBaseItem label="时间" value={formatDateTime(payment?.updatedTime)} />
                    {payment?.state === CUSTOMER_ADVANCE_PAY_STATE.REVOKE.code ? (
                        <ZhBaseItem label="撤销备注" span={4} value={payment.remark || DEFAULT_VALUE} />
                    ) : null}
                </ZhGrid>
            </div>
            <Space>
                {payment?.state === CUSTOMER_ADVANCE_PAY_STATE.SUCCESS.code ? (
                    <Permission anyOf={["ROLE_ACCOUNT_MANAGE"]}>
                        <Button
                            disabled={banButton}
                            type="primary"
                            onClick={() => {
                                setRevokeOpen(true);
                                setRevokeReason("");
                                setRevokeError("");
                            }}
                        >
                            撤销
                        </Button>
                    </Permission>
                ) : null}
                <Button onClick={() => void navigate(-1)}>返回</Button>
            </Space>
            <Modal
                title="撤销"
                open={revokeOpen}
                okText="确定"
                cancelText="取消"
                confirmLoading={revokeLoading}
                okButtonProps={{ disabled: banButton }}
                onCancel={() => setRevokeOpen(false)}
                onOk={() => void submitRevoke()}
            >
                <Typography.Text>撤销后账户余额将发生变化，请谨慎操作</Typography.Text>
                <Input.TextArea
                    className="mt-4"
                    aria-label="撤销原因"
                    maxLength={100}
                    placeholder="请输入撤销原因"
                    rows={3}
                    value={revokeReason}
                    onChange={event => setRevokeReason(event.target.value)}
                />
                {revokeError ? <Alert className="mt-2" message={revokeError} role="alert" type="error" /> : null}
            </Modal>
        </section>
    );
}

export default UserAccountAdvanceDetailPage;
